import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { node_imageBaseUrl } from "../pages/const";

// Match other PDF components: Gujarati + default Latin fonts
Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Regular.ttf",
});

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Bold.ttf",
  fontWeight: "bold",
});

const BORDER = "#000000";
const BORDER_PT = `1pt solid ${BORDER}`;
const TABLE_ROWS = 6;

const resolveImageUrl = (rawPath) => {
  if (!rawPath) return null;
  const p = String(rawPath).trim();
  if (!p) return null;
  if (
    p.startsWith("data:") ||
    p.startsWith("blob:") ||
    p.startsWith("http://") ||
    p.startsWith("https://")
  ) {
    return p;
  }
  const base = String(node_imageBaseUrl || "").replace(/\/+$/, "");
  if (!base) return p.startsWith("/") ? p : `/${p}`;
  return `${base}/${p.replace(/^\/+/, "")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d)) return String(dateStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return String(dateStr);
  }
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatWeight = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  if (num === 0) return "";
  return num.toFixed(2);
};

const formatPurity = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return String(value);
  return num % 1 === 0 ? String(num) : num.toFixed(2);
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 18,
    paddingBottom: 18,
    paddingHorizontal: 22,
    fontFamily: "Helvetica",
    fontSize: 9,
    backgroundColor: "#ffffff",
    color: "#000000",
  },

  // ── HEADER ──────────────────────────────────────────────
  topRow: {
    position: "relative",
    minHeight: 36,
    marginBottom: 10,
  },
  titleWrap: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 2,
  },
  centerTitle: {
    textAlign: "center",
    fontSize: 22,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    letterSpacing: 2,
    color: "#000000",
  },
  slipNoBox: {
    position: "absolute",
    right: 0,
    top: 0,
    alignItems: "flex-end",
  },
  slipNoText: {
    fontSize: 9,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    marginBottom: 2,
    color: "#000000",
  },

  // ── CUSTOMER DETAILS (InvoicePDF-style rounded box) ─────
  customerBox: {
    border: BORDER_PT,
    borderRadius: 8,
    marginBottom: 8,
  },
  customerHeader: {
    borderBottom: BORDER_PT,
    paddingVertical: 3,
    paddingHorizontal: 4,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    // Match outer curve so grey fill is not square-clipped at top
    borderTopLeftRadius: 7,
    borderTopRightRadius: 7,
  },
  customerHeaderText: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    textAlign: "center",
    color: "#000000",
  },
  customerInner: {
    flexDirection: "row",
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 6,
    borderBottomLeftRadius: 7,
    borderBottomRightRadius: 7,
  },
  customerFields: {
    flex: 1,
    paddingRight: 8,
  },
  fieldLine: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 5,
    minHeight: 14,
  },
  fieldLabel: {
    fontSize: 9,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    width: 88,
    flexShrink: 0,
    color: "#000000",
  },
  fieldValue: {
    fontSize: 9,
    fontFamily: "Helvetica",
    fontWeight: "bold",
    flex: 1,
    borderBottom: "0.5pt solid #000000",
    minHeight: 12,
    paddingBottom: 1,
    color: "#000000",
  },
  photoBox: {
    width: 78,
    height: 90,
    border: BORDER_PT,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: 4,
  },
  photoImage: {
    width: "100%",
    height: "100%",
    maxWidth: 70,
    maxHeight: 80,
    objectFit: "contain",
  },
  photoLabel: {
    fontSize: 7,
    textAlign: "center",
    paddingHorizontal: 4,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    color: "#000000",
  },

  // ── LOAN SUMMARY ROW ────────────────────────────────────
  loanRow: {
    flexDirection: "row",
    border: BORDER_PT,
    borderRadius: 8,
    marginBottom: 8,
  },
  loanCell: {
    flex: 1,
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRight: BORDER_PT,
  },
  loanCellFirst: {
    flex: 1,
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRight: BORDER_PT,
    borderTopLeftRadius: 7,
    borderBottomLeftRadius: 7,
  },
  loanCellLast: {
    flex: 1,
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderTopRightRadius: 7,
    borderBottomRightRadius: 7,
  },
  loanLabel: {
    fontSize: 8,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    marginBottom: 3,
    textAlign: "center",
    color: "#000000",
  },
  loanValue: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    textAlign: "center",
    color: "#000000",
  },

  // ── ITEMS TABLE (CustomOrder / Invoice rounded table) ───
  table: {
    border: BORDER_PT,
    borderRadius: 6,
    marginBottom: 10,
  },
  tableMainHeader: {
    borderBottom: BORDER_PT,
    paddingVertical: 4,
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
  },
  tableMainHeaderText: {
    fontSize: 10,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    color: "#000000",
  },
  tableColHeader: {
    flexDirection: "row",
    borderBottom: BORDER_PT,
    backgroundColor: "#f5f5f5",
    minHeight: 20,
    alignItems: "center",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: BORDER_PT,
    minHeight: 22,
  },
  tableRowLast: {
    flexDirection: "row",
    minHeight: 22,
    borderBottomLeftRadius: 5,
    borderBottomRightRadius: 5,
  },
  colNo: {
    width: "8%",
    borderRight: BORDER_PT,
    paddingVertical: 4,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  colName: {
    width: "40%",
    borderRight: BORDER_PT,
    paddingVertical: 4,
    paddingHorizontal: 3,
    justifyContent: "center",
  },
  colPurity: {
    width: "18%",
    borderRight: BORDER_PT,
    paddingVertical: 4,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  colGross: {
    width: "17%",
    borderRight: BORDER_PT,
    paddingVertical: 4,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  colNet: {
    width: "17%",
    paddingVertical: 4,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  colHeaderText: {
    fontSize: 8,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    textAlign: "center",
    color: "#000000",
  },
  cellText: {
    fontSize: 8,
    fontFamily: "Helvetica",
    textAlign: "center",
    color: "#000000",
  },
  cellTextLeft: {
    fontSize: 8,
    fontFamily: "NotoSansGujaratiUI",
    textAlign: "left",
    color: "#000000",
  },

  // ── NOTE ────────────────────────────────────────────────
  noteRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
    minHeight: 36,
  },
  noteLabel: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    marginRight: 4,
    color: "#000000",
  },
  noteLine: {
    flex: 1,
    borderBottom: "0.5pt solid #000000",
    minHeight: 34,
  },
  noteValue: {
    fontSize: 8,
    fontFamily: "Helvetica",
    color: "#000000",
  },

  // ── SIGNATURES ──────────────────────────────────────────
  signatureArea: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 48,
    marginBottom: 14,
    paddingHorizontal: 4,
  },
  signBox: {
    width: "42%",
    alignItems: "center",
  },
  signUnderline: {
    width: "100%",
    borderTop: BORDER_PT,
    paddingTop: 4,
  },
  signLabel: {
    fontSize: 8,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    textAlign: "center",
    color: "#000000",
  },
  rulesDivider: {
    borderTop: BORDER_PT,
    marginBottom: 8,
  },

  // ── RULES ───────────────────────────────────────────────
  rulesSection: {
    marginTop: 4,
  },
  rulesTitle: {
    fontSize: 11,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    marginBottom: 4,
    color: "#000000",
  },
  ruleRow: {
    flexDirection: "row",
    marginBottom: 3,
    alignItems: "flex-start",
  },
  ruleNum: {
    fontSize: 9,
    width: 18,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    color: "#000000",
  },
  ruleText: {
    fontSize: 9,
    flex: 1,
    lineHeight: 1.35,
    fontFamily: "NotoSansGujaratiUI",
    color: "#000000",
  },
});

const RULES = [
  "રસીદ ફરજીયાત લાવવાની રહેશે.",
  "રસીદ વગર વહિવટ થશે નહિ.",
  "દર મહિને વ્યાજ ની રકમ ફરજીયાત જમા કરાવવાની રહેશે.",
  "3 મહિના સુધી વ્યાજ જમા નહિ થાય તો ચક્રવૃદ્ધિ વ્યાજ લાગશે.",
  "દાગીના છોડાવવા માટે 2 થી 3 દિવસ પહેલા જાણ કરવાની રહેશે.",
  "શુક્ર, શનિ, રવિ અથવા જાહેર રજા ના દિવસે વસ્તુ મળશે નહિ.",
];

const padItems = (items) => {
  const list = Array.isArray(items) ? [...items] : [];
  while (list.length < TABLE_ROWS) list.push(null);
  return list.slice(0, Math.max(TABLE_ROWS, list.length));
};

const CustomerField = ({ label, value }) => (
  <View style={styles.fieldLine}>
    <Text style={styles.fieldLabel}>{label} :</Text>
    <Text style={styles.fieldValue}>{value || " "}</Text>
  </View>
);

const DhiranSlipPDF = ({ girviData, variant = "girvi" }) => {
  if (!girviData) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={{ fontFamily: "NotoSansGujaratiUI" }}>
            કોઈ ડેટા ઉપલબ્ધ નથી
          </Text>
        </Page>
      </Document>
    );
  }

  const items = padItems(girviData.items || []);
  const today = new Date();
  const todayStr = `${String(today.getDate()).padStart(2, "0")}/${String(
    today.getMonth() + 1,
  ).padStart(2, "0")}/${today.getFullYear()}`;

  const idProofParts = [
    girviData.adhar_card || girviData.aadhar_no || girviData.aadharNo,
    girviData.pan_card || girviData.pan_no || girviData.panNo,
  ].filter(Boolean);
  const idProof = idProofParts.join(" / ");

  const itemImageUrl = resolveImageUrl(
    girviData.items_image ||
      girviData.item_image ||
      (Array.isArray(girviData.items) &&
        girviData.items.find((it) => it?.image)?.image) ||
      null,
  );

  // Prefer item image in the customer-details photo slot
  const photoUrl =
    itemImageUrl ||
    resolveImageUrl(
      girviData.customer_photo ||
        girviData.photo_url ||
        girviData.photo ||
        girviData.customer_image ||
        girviData.image_url ||
        null,
    );

  const isReverse = variant === "reverse";
  const principal = Number(
    isReverse
      ? (girviData.principal_taken ??
        girviData.principal_amount ??
        girviData.principal_given)
      : girviData.principal_given,
  );
  const interest = Number(girviData.interest_percent);
  const slipNumber =
    (isReverse && girviData.reverse_girvi_number) ||
    girviData.girvi_number ||
    "-";
  const slipDate =
    (isReverse &&
      (girviData.reverse_girvi_date ||
        girviData.start_date ||
        girviData.doc_date)) ||
    girviData.girvi_date;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header: title + number / date */}
        <View style={styles.topRow}>
          <View style={styles.titleWrap}>
            <Text style={styles.centerTitle}>
              {isReverse ? "રિવર્સ ધિરાણ" : "ધિરાણ"}
            </Text>
          </View>
          <View style={styles.slipNoBox}>
            <Text style={styles.slipNoText}>
              નંબર : {slipNumber}
            </Text>
            <Text style={styles.slipNoText}>
              તારીખ : {formatDate(slipDate) || todayStr}
            </Text>
          </View>
        </View>

        {/* Customer Details — Invoice-style box with header bar */}
        <View style={styles.customerBox}>
          <View style={styles.customerHeader}>
            <Text style={styles.customerHeaderText}>Customer Details</Text>
          </View>
          <View style={styles.customerInner}>
            <View style={styles.customerFields}>
              <CustomerField label="નામ" value={girviData.customer_name} />
              <CustomerField
                label="સરનામું"
                value={girviData.customer_address}
              />
              <CustomerField
                label="મો. નંબર"
                value={girviData.customer_mobile}
              />
              <CustomerField label="આધાર/પાન કાર્ડ" value={idProof} />
            </View>
            <View style={styles.photoBox}>
              {photoUrl ? (
                <Image src={photoUrl} style={styles.photoImage} />
              ) : (
                <Text style={styles.photoLabel}>હાજરના Photo</Text>
              )}
            </View>
          </View>
        </View>

        {/* Loan summary row */}
        <View style={styles.loanRow}>
          <View style={styles.loanCellFirst}>
            <Text style={styles.loanLabel}>
              {isReverse ? "લીધેલ રકમ" : "લોન રકમ"}
            </Text>
            <Text style={styles.loanValue}>
              {Number.isFinite(principal)
                ? `Rs. ${formatAmount(principal)}`
                : "-"}
            </Text>
          </View>
          <View style={styles.loanCell}>
            <Text style={styles.loanLabel}>માસિક વ્યાજ દર (%)</Text>
            <Text style={styles.loanValue}>
              {Number.isFinite(interest) ? `${interest}%` : "-"}
            </Text>
          </View>
          <View style={styles.loanCellLast}>
            <Text style={styles.loanLabel}>લોન પાકવાની તારીખ</Text>
            <Text style={styles.loanValue}>
              {formatDate(girviData.due_date)}
            </Text>
          </View>
        </View>

        {/* Items table — normal Girvi only (Reverse Girvi has no pledge ornaments) */}
        {!isReverse ? (
          <View style={styles.table}>
            <View style={styles.tableMainHeader}>
              <Text style={styles.tableMainHeaderText}>ધિરાણની વિગત</Text>
            </View>

            <View style={styles.tableColHeader}>
              <View style={styles.colNo}>
                <Text style={styles.colHeaderText}>No</Text>
              </View>
              <View style={styles.colName}>
                <Text style={styles.colHeaderText}>વસ્તુ નું નામ</Text>
              </View>
              <View style={styles.colPurity}>
                <Text style={styles.colHeaderText}>Purity / શુદ્ધતા</Text>
              </View>
              <View style={styles.colGross}>
                <Text style={styles.colHeaderText}>ગ્રોસ વજન</Text>
              </View>
              <View style={styles.colNet}>
                <Text style={styles.colHeaderText}>નેટ વજન</Text>
              </View>
            </View>

            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              const rowStyle = isLast ? styles.tableRowLast : styles.tableRow;
              return (
                <View style={rowStyle} key={index} wrap={false}>
                  <View style={styles.colNo}>
                    <Text style={styles.cellText}>{index + 1}</Text>
                  </View>
                  <View style={styles.colName}>
                    <Text style={styles.cellTextLeft}>
                      {item ? item.name || item.description || "" : ""}
                    </Text>
                  </View>
                  <View style={styles.colPurity}>
                    <Text style={styles.cellText}>
                      {item ? formatPurity(item.purity) : ""}
                    </Text>
                  </View>
                  <View style={styles.colGross}>
                    <Text style={styles.cellText}>
                      {item ? formatWeight(item.gross_weight) : ""}
                    </Text>
                  </View>
                  <View style={styles.colNet}>
                    <Text style={styles.cellText}>
                      {item ? formatWeight(item.net_weight) : ""}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        ) : null}

        {/* Note */}
        <View style={styles.noteRow}>
          <Text style={styles.noteLabel}>Note :</Text>
          <View style={styles.noteLine}>
            {girviData.note || girviData.remarks ? (
              <Text style={styles.noteValue}>
                {girviData.note || girviData.remarks}
              </Text>
            ) : null}
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signatureArea}>
          <View style={styles.signBox}>
            <View style={styles.signUnderline}>
              <Text style={styles.signLabel}>
                દેવાદારની સહી અથવા અંગૂઠાનું નિશાન
              </Text>
            </View>
          </View>
          <View style={styles.signBox}>
            <View style={styles.signUnderline}>
              <Text style={styles.signLabel}>
                નાણા ધીરનારની સહી અથવા અંગૂઠાનું નિશાન
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.rulesDivider} />

        {/* Rules */}
        <View style={styles.rulesSection}>
          <Text style={styles.rulesTitle}>નિયમો :-</Text>
          {RULES.map((rule, i) => (
            <View style={styles.ruleRow} key={i}>
              <Text style={styles.ruleNum}>{i + 1} )</Text>
              <Text style={styles.ruleText}>{rule}</Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default DhiranSlipPDF;
